import { EMAIL } from "@/lib/constants";
import {
  LegalH3,
  LegalLI,
  LegalP,
  LegalUL,
} from "@/components/sections/editorial/legal/post/parts";

type RichTextSpan = { type: string; start: number; end: number };
export type RichTextNode = { type: string; text: string; spans: RichTextSpan[] };

export type PrismicBlock = {
  heading?: string | null;
  body?: RichTextNode[];
  table_html?: string | null;
};

// Fallback copy, used only if the "know-your-customer-kyc-verificationa-proxidize"
// legal_doc can't be fetched from Prismic. Prismic content_blocks are the source of truth.
export const KYC_INTRO = (
  <LegalP>
    At Proxidize, verification keeps our network safe, lawful, and trusted. This page explains what
    verification is, why we do it, who it applies to, and how our verification tiers work.
  </LegalP>
);

export const SECTIONS = [
  {
    id: "why-we-verify",
    heading: "Why we verify",
    content: (
      <>
        <LegalP>
          We verify our customers to protect the network and everyone who relies on it. Verification
          helps us:
        </LegalP>
        <LegalUL>
          <LegalLI>Confirm that customers are who they say they are;</LegalLI>
          <LegalLI>Prevent fraud, abuse, and unlawful use of the proxies;</LegalLI>
          <LegalLI>
            Meet our legal, regulatory, and contractual obligations, including anti-money-laundering
            and sanctions requirements;
          </LegalLI>
          <LegalLI>
            Uphold the ethical standards behind how our network is sourced and operated (see our
            Ethics page at https://proxidize.com/ethics/); and
          </LegalLI>
          <LegalLI>
            Support our information-security commitments, including ISO/IEC 27001 and SOC 2.
          </LegalLI>
        </LegalUL>
      </>
    ),
  },
  {
    id: "who-this-applies-to",
    heading: "Who this applies to",
    content: (
      <LegalP>
        Verification applies to all users who want to access the proxy Services. You must complete
        identity verification (Tier 1) before you can use the proxies. Access to certain sensitive
        categories, and to higher usage, requires business verification (Tier 2 / KYB).
      </LegalP>
    ),
  },
  {
    id: "verification-tiers-at-a-glance",
    heading: "Verification tiers at a glance",
    content: (
      <>
        <LegalH3>Tier 1 (Identity / KYC)</LegalH3>
        <LegalUL>
          <LegalLI>Who it is for: all users, required for proxy access.</LegalLI>
          <LegalLI>
            What you provide: a valid government-issued photo ID, a device with a camera, and your
            country of residence.
          </LegalLI>
          <LegalLI>Connection threads: limited.</LegalLI>
          <LegalLI>Category access: general web destinations.</LegalLI>
          <LegalLI>How to complete: self-service in your account.</LegalLI>
        </LegalUL>
        <LegalH3>Tier 2 (Business / KYB)</LegalH3>
        <LegalUL>
          <LegalLI>
            Who it is for: businesses that need sensitive categories or higher usage.
          </LegalLI>
          <LegalLI>What you provide: business and entity verification details.</LegalLI>
          <LegalLI>Connection threads: unlimited.</LegalLI>
          <LegalLI>Category access: unlocks the verification-gated categories below.</LegalLI>
          <LegalLI>How to complete: contact our sales team.</LegalLI>
        </LegalUL>
      </>
    ),
  },
  {
    id: "tier-1-identity-verification-kyc",
    heading: "Tier 1 (Identity Verification / KYC)",
    content: (
      <>
        <LegalH3>Requirements</LegalH3>
        <LegalP>
          A valid government-issued photo ID (passport, national ID card, or driver&apos;s license),
          a device with a camera, and your current country of residence.
        </LegalP>
        <LegalH3>What to expect</LegalH3>
        <LegalP>
          Verification is a short, guided process inside your Proxidize account: you select your
          country, photograph your ID, and complete a quick selfie check. Your status will show as
          Verify, Verified, Rejected, or Expired (an incomplete session times out and can be
          restarted). For the full step-by-step, see our Help Center.
        </LegalP>
        <LegalH3>What Tier 1 gives you</LegalH3>
        <LegalP>
          Access to general web destinations, subject to connection-thread limits. The permanently
          restricted categories below remain blocked, and the verification-gated categories are not
          available at this tier.
        </LegalP>
        <LegalH3>Moving up</LegalH3>
        <LegalP>
          To access the verification-gated categories or to remove thread limits, upgrade to Tier 2.
        </LegalP>
      </>
    ),
  },
  {
    id: "tier-2-business-verification-kyb",
    heading: "Tier 2 (Business Verification / KYB)",
    content: (
      <>
        <LegalH3>Requirements</LegalH3>
        <LegalP>
          Verification of your business or entity. We are finalising this process; to get started,
          contact our sales team.
        </LegalP>
        <LegalH3>What to expect</LegalH3>
        <LegalP>
          Our team will guide you through business verification and confirm eligibility for the
          verification-gated categories and higher usage.
        </LegalP>
        <LegalH3>What Tier 2 gives you</LegalH3>
        <LegalP>
          Unlimited connection threads and access to the verification-gated categories below. The
          permanently restricted categories remain blocked at all tiers.
        </LegalP>
        <LegalH3>How to start</LegalH3>
        <LegalP>Contact sales to begin Tier 2 (KYB) verification.</LegalP>
      </>
    ),
  },
  {
    id: "restricted-and-verification-gated-categories",
    heading: "Restricted and verification-gated categories",
    content: (
      <>
        <LegalP>
          To keep the network safe and compliant, some categories of website are restricted or gated
          by tier:
        </LegalP>
        <LegalUL>
          <LegalLI>Adult: blocked for all users.</LegalLI>
          <LegalLI>Survey: blocked for all users.</LegalLI>
          <LegalLI>Government: blocked for all users.</LegalLI>
          <LegalLI>Military: blocked for all users.</LegalLI>
          <LegalLI>Financial: requires Tier 2 (KYB).</LegalLI>
          <LegalLI>HR / recruitment: requires Tier 2 (KYB).</LegalLI>
          <LegalLI>Consumer reviews: requires Tier 2 (KYB).</LegalLI>
        </LegalUL>
        <LegalP>
          These controls apply in addition to our Acceptable Use Policy (
          https://proxidize.com/legal/acceptable-use-policy/), which sets out how the Services may
          and may not be used.
        </LegalP>
      </>
    ),
  },
  {
    id: "what-happens-if-you-do-not-verify",
    heading: "What happens if you do not verify",
    content: (
      <LegalP>
        If you do not complete Tier 1 identity verification, you will not be able to access the
        proxy Services. The permanently restricted categories stay blocked regardless of tier, and
        the verification-gated categories stay blocked until Tier 2 is completed.
      </LegalP>
    ),
  },
  {
    id: "connectivity-and-technical-details",
    heading: "Connectivity and technical details",
    content: (
      <LegalP>
        For technical details such as supported ports and connectivity, please see our Help Center.
      </LegalP>
    ),
  },
  {
    id: "questions-and-support",
    heading: "Questions and support",
    content: (
      <LegalUL>
        <LegalLI>General verification help: visit our Help Center or contact support.</LegalLI>
        <LegalLI>Business verification (Tier 2 / KYB): contact our sales team.</LegalLI>
        <LegalLI>
          To report misuse of the Services: {EMAIL.abuse} or https://proxidize.com/report-abuse/.
        </LegalLI>
      </LegalUL>
    ),
  },
];
